import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Link2,
  ChevronDown,
  ChevronUp,
  Clock,
  Video,
  Phone,
  MapPin,
  Settings2,
  Edit3,
  Plus,
  Check,
  CalendarPlus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Copy,
  ExternalLink,
} from "lucide-react";
import { bookingTypesApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";
import BookingTypeModal from "./BookingTypeModal";
import Toggle from "../ui/Toggle";

// ─── Types ──────────────────────────────────────────────────────────────────

interface BookingTypeLocation {
  type: "in_person" | "phone" | "video" | "custom";
  details: string | null;
}

interface BookingType {
  _id: string;
  name: string;
  slug: string;
  description: string | null;
  duration: number;
  location: BookingTypeLocation;
  color: string | null;
  isActive: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const STORAGE_KEY = "kavoa-scheduling-links-expanded";

const getInitialExpanded = (): boolean => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? false : stored === "true";
  } catch {
    return false;
  }
};

const LOCATION_ICON: Record<string, React.ReactNode> = {
  video: <Video className="h-3 w-3" />,
  phone: <Phone className="h-3 w-3" />,
  in_person: <MapPin className="h-3 w-3" />,
  custom: <Settings2 className="h-3 w-3" />,
};

// ─── Component ──────────────────────────────────────────────────────────────

const SchedulingLinksSection: React.FC = () => {
  const { firmData } = useUserDataContext();

  const [bookingTypes, setBookingTypes] = useState<BookingType[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(getInitialExpanded);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Success popup state
  const [successInfo, setSuccessInfo] = useState<{ name: string; url: string } | null>(null);
  const [successCopied, setSuccessCopied] = useState(false);

  // ── Fetch booking types ─────────────────────────────────────────────────

  const fetchBookingTypes = useCallback(async () => {
    try {
      const res = await bookingTypesApi.list();
      const data: BookingType[] = res.data?.data || res.data || [];
      setBookingTypes(data);
    } catch (err) {
      console.error("Failed to fetch booking types:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookingTypes();
  }, [fetchBookingTypes]);

  // ── Expand / collapse persistence ───────────────────────────────────────

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  // ── Copy link handler ───────────────────────────────────────────────────

  const handleCopyLink = async (bt: BookingType) => {
    const slug = firmData.slug || firmData._id;
    const url = `${window.location.origin}/book/${slug}/${bt.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(bt._id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  // ── Toggle active handler ───────────────────────────────────────────────

  const handleToggleActive = async (bt: BookingType) => {
    try {
      setTogglingId(bt._id);
      await bookingTypesApi.update(bt._id, { isActive: !bt.isActive });
      setBookingTypes((prev) =>
        prev.map((item) =>
          item._id === bt._id ? { ...item, isActive: !item.isActive } : item
        )
      );
    } catch (err) {
      console.error("Failed to toggle booking type:", err);
    } finally {
      setTogglingId(null);
    }
  };

  // ── Delete handler ───────────────────────────────────────────────────

  const handleDelete = async (id: string) => {
    try {
      await bookingTypesApi.remove(id);
      setBookingTypes((prev) => prev.filter((bt) => bt._id !== id));
    } catch (err) {
      console.error("Failed to delete booking type:", err);
    } finally {
      setDeletingId(null);
    }
  };

  // ── Modal handlers ────────────────────────────────────────────────────

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingId(null);
  };

  const handleModalSave = (created?: { name: string; slug: string }) => {
    handleModalClose();
    fetchBookingTypes();
    if (created) {
      const firmSlug = firmData.slug || firmData._id;
      const url = `${window.location.origin}/book/${firmSlug}/${created.slug}`;
      setSuccessInfo({ name: created.name, url });
      setSuccessCopied(false);
      // Auto-expand the list so user can see the new entry
      if (!expanded) {
        setExpanded(true);
        try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
      }
    }
  };

  const handleSuccessCopy = async () => {
    if (!successInfo) return;
    try {
      await navigator.clipboard.writeText(successInfo.url);
      setSuccessCopied(true);
      setTimeout(() => setSuccessCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 px-4 py-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-emerald-600" />
          <span className="text-xs text-slate-500">Loading scheduling links…</span>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-4 overflow-hidden">
        {/* ── Header row ──────────────────────────────────────────────── */}
        <button
          onClick={toggleExpanded}
          className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left hover:bg-slate-50/60 transition-colors"
        >
          <div
            className="h-7 w-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "rgba(15, 81, 50, 0.08)" }}
          >
            <Link2
              className="h-3.5 w-3.5"
              style={{ color: "var(--color-primary)" }}
            />
          </div>

          <div className="min-w-0">
            <span className="text-sm font-semibold text-slate-800">
              Booking Links
            </span>
            <span className="hidden sm:inline text-xs text-slate-400 ml-2">
              Share a link so clients can schedule with you
            </span>
          </div>

          {bookingTypes.length > 0 && (
            <span
              className="inline-flex items-center justify-center h-[18px] min-w-[18px] px-1 rounded-full text-[10px] font-bold text-white leading-none"
              style={{ backgroundColor: "var(--color-accent)" }}
            >
              {bookingTypes.length}
            </span>
          )}

          {/* Quick-create from header */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowCreateModal(true);
            }}
            className="ml-auto mr-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-white transition-colors"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor =
                "var(--color-primary-hover)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor =
                "var(--color-primary)")
            }
          >
            <Plus className="h-3 w-3" />
            New
          </button>

          <span className="text-slate-400">
            {expanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </span>
        </button>

        {/* ── Expanded body ──────────────────────────────────────────── */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="scheduling-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-3 border-t border-slate-100">
                {bookingTypes.length === 0 ? (
                  /* ── Empty state ─────────────────────────────────── */
                  <div className="flex items-center gap-3 py-3">
                    <CalendarPlus
                      className="h-4 w-4 flex-shrink-0"
                      style={{ color: "var(--color-primary)" }}
                    />
                    <p className="text-sm text-slate-500">
                      No scheduling links yet — click <span className="font-semibold text-slate-700">New</span> to create one.
                    </p>
                  </div>
                ) : (
                  /* ── Compact card list ──────────────────────────── */
                  <div className="divide-y divide-slate-100 pt-1">
                    {bookingTypes.map((bt) => {
                      const locIcon =
                        LOCATION_ICON[bt.location?.type] ||
                        LOCATION_ICON.video;
                      const isCopied = copiedId === bt._id;
                      const isToggling = togglingId === bt._id;

                      return (
                        <div
                          key={bt._id}
                          className="flex items-center gap-3 py-2.5 group"
                        >
                          {/* Color dot */}
                          <span
                            className="h-2 w-2 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor:
                                bt.color || "var(--color-accent)",
                            }}
                          />

                          {/* Name */}
                          <span className="text-sm font-medium text-slate-800 truncate min-w-0">
                            {bt.name}
                          </span>

                          {/* Duration + Location pills */}
                          <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-50 text-[11px] text-slate-500 flex-shrink-0">
                            <Clock className="h-2.5 w-2.5" />
                            {bt.duration}m
                          </span>
                          <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded bg-slate-50 text-[11px] text-slate-500 flex-shrink-0">
                            {locIcon}
                          </span>

                          {/* Status */}
                          <span
                            className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                              bt.isActive
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-400"
                            }`}
                          >
                            {bt.isActive ? "Active" : "Off"}
                          </span>

                          {/* Spacer */}
                          <div className="flex-1" />

                          {/* Actions */}
                          <div className="flex items-center gap-0.5">
                            {/* Copy link */}
                            <button
                              onClick={() => handleCopyLink(bt)}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                                isCopied
                                  ? "text-emerald-600 bg-emerald-50"
                                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                              }`}
                              title="Copy booking link"
                            >
                              {isCopied ? (
                                <>
                                  <Check className="h-3 w-3" />
                                  Copied
                                </>
                              ) : (
                                <>
                                  <Link2 className="h-3 w-3" />
                                  Copy
                                </>
                              )}
                            </button>

                            {/* Edit */}
                            <button
                              onClick={() => setEditingId(bt._id)}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                              title="Edit"
                            >
                              <Edit3 className="h-3 w-3" />
                            </button>

                            {/* Delete */}
                            <button
                              onClick={() => setDeletingId(bt._id)}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>

                            {/* Toggle */}
                            <Toggle
                              checked={bt.isActive}
                              onChange={() => handleToggleActive(bt)}
                              size="sm"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Delete Confirmation Popup ──────────────────────────────────── */}
      <AnimatePresence>
        {deletingId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setDeletingId(null)}
            />

            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="relative bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-full max-w-sm mx-4"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Delete Booking Link
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Are you sure you want to delete{" "}
                    <span className="font-medium text-slate-700">
                      {bookingTypes.find((bt) => bt._id === deletingId)?.name}
                    </span>
                    ? This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  onClick={() => setDeletingId(null)}
                  className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deletingId && handleDelete(deletingId)}
                  className="px-3.5 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Success Popup ────────────────────────────────────────────── */}
      <AnimatePresence>
        {successInfo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/30"
              onClick={() => setSuccessInfo(null)}
            />

            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="relative bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-full max-w-md mx-4"
            >
              {/* Checkmark */}
              <div className="flex flex-col items-center text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 20 }}
                  className="h-14 w-14 rounded-full flex items-center justify-center mb-4"
                  style={{ backgroundColor: "rgba(15, 81, 50, 0.08)" }}
                >
                  <CheckCircle2
                    className="h-7 w-7"
                    style={{ color: "var(--color-primary)" }}
                  />
                </motion.div>

                <h3 className="text-base font-semibold text-slate-900">
                  Booking Link Created
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  <span className="font-medium text-slate-700">{successInfo.name}</span> is now live and ready to share.
                </p>
              </div>

              {/* URL display + copy */}
              <div className="mt-4 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                <Link2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700 truncate flex-1 font-mono">
                  {successInfo.url}
                </span>
                <button
                  onClick={handleSuccessCopy}
                  className={`flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    successCopied
                      ? "text-emerald-600 bg-emerald-50"
                      : "text-slate-600 hover:text-slate-800 hover:bg-slate-200"
                  }`}
                >
                  {successCopied ? (
                    <>
                      <Check className="h-3.5 w-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </>
                  )}
                </button>
              </div>

              {/* Actions */}
              <div className="mt-5 flex items-center justify-center gap-2">
                <button
                  onClick={() => window.open(successInfo.url, "_blank")}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Preview
                </button>
                <button
                  onClick={() => setSuccessInfo(null)}
                  className="px-3.5 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "var(--color-primary)")
                  }
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Booking Type Modal ────────────────────────────────────────── */}
      <BookingTypeModal
        open={showCreateModal || !!editingId}
        editId={editingId}
        onClose={handleModalClose}
        onSave={handleModalSave}
      />
    </>
  );
};

export default SchedulingLinksSection;
